import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, FormGroup, Input, InputGroup, InputGroupText, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import DefaultButton from "../../../../../components/Buttons/Default";
import { post } from "../../../../../utils/helpers/api";
import { toast } from "react-toastify";
import { useSettings } from "../../../../../context/settings";

const emptyBenefitType = (type) => ({
  name: '',
  code: '',
  type,
  calculation_type: 'fixed',
  value: '',
  percentage_base: 'basic_salary',
  is_taxable: false,
  affects_social_security: false,
  is_active: true,
  description: '',
});

function CreateBenefitTypeModal({ isOpen, toggle, refreshSettings, benefitTypeToEdit, type }) {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [saveLoading, setSaveLoading] = useState(false);
  const [benefitData, setBenefitData] = useState(emptyBenefitType(type));

  const currency = settings?.sales?.base_currency || '';
  const isAllowance = type === 'allowance';
  const isPercentage = benefitData.calculation_type === 'percentage';

  useEffect(() => {
    if (benefitTypeToEdit) {
      setBenefitData({
        name: benefitTypeToEdit.name || '',
        code: benefitTypeToEdit.code || '',
        type: benefitTypeToEdit.type || type,
        calculation_type: benefitTypeToEdit.calculation_type || 'fixed',
        value: benefitTypeToEdit.value ?? '',
        percentage_base: benefitTypeToEdit.percentage_base || 'basic_salary',
        is_taxable: !!benefitTypeToEdit.is_taxable,
        affects_social_security: !!benefitTypeToEdit.affects_social_security,
        is_active: benefitTypeToEdit.is_active ?? true,
        description: benefitTypeToEdit.description || '',
      });
    } else {
      setBenefitData(emptyBenefitType(type));
    }
  }, [benefitTypeToEdit, isOpen, type]);

  const updateField = (field, value) => setBenefitData({ ...benefitData, [field]: value });

  const saveBenefitType = async () => {
    setSaveLoading(true);
    const url = benefitTypeToEdit
      ? `/hr/benefit-types/update/${benefitTypeToEdit.id}`
      : '/hr/benefit-types/store';
    const data = await post(url, { ...benefitData, value: Number(benefitData.value) || 0 });
    if (data?.success) {
      refreshSettings();
      toggle();
      toast.success(benefitTypeToEdit ? t('alerts.success_update') : t('alerts.success_create'));
    } else {
      toast.error(data?.message || t('alerts.error'));
      toggle();
    }
    setSaveLoading(false);
  }

  const title = benefitTypeToEdit
    ? (isAllowance ? t('hr.settings.allowances.edit') : t('hr.settings.deductions.edit'))
    : (isAllowance ? t('hr.settings.allowances.create_new') : t('hr.settings.deductions.create_new'));

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>{title}</ModalHeader>

      <ModalBody>
        <Row>
          <Col md="8">
            <FormGroup>
              <Label>{t('common.name')}: </Label>
              <Input
                value={benefitData.name}
                onChange={e => updateField('name', e.target.value)}
                placeholder={isAllowance ? t('hr.benefits.namePlaceholderAllowance') : t('hr.benefits.namePlaceholderDeduction')}
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label>{t('hr.benefits.code')}: </Label>
              <Input
                value={benefitData.code}
                onChange={e => updateField('code', e.target.value.toUpperCase())}
                placeholder={isAllowance ? 'HRA' : 'SS'}
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="4">
            <FormGroup>
              <Label>{t('hr.benefits.calculationType')}: </Label>
              <Input
                type="select"
                value={benefitData.calculation_type}
                onChange={e => updateField('calculation_type', e.target.value)}
              >
                <option value="fixed">{t('hr.benefits.fixed')}</option>
                <option value="percentage">{t('hr.benefits.percentage')}</option>
              </Input>
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label>{t('hr.benefits.value')}: </Label>
              <InputGroup>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={benefitData.value}
                  onChange={e => updateField('value', e.target.value)}
                />
                <InputGroupText>{isPercentage ? '%' : currency}</InputGroupText>
              </InputGroup>
            </FormGroup>
          </Col>
          {isPercentage && (
            <Col md="4">
              <FormGroup>
                <Label>{t('hr.benefits.percentageBase')}: </Label>
                <Input
                  type="select"
                  value={benefitData.percentage_base}
                  onChange={e => updateField('percentage_base', e.target.value)}
                >
                  <option value="basic_salary">{t('hr.benefits.basicSalary')}</option>
                  <option value="gross_salary">{t('hr.benefits.grossSalary')}</option>
                </Input>
              </FormGroup>
            </Col>
          )}
        </Row>

        <FormGroup>
          <Label>{t('common.description')}: </Label>
          <Input
            type="textarea"
            rows="2"
            value={benefitData.description}
            onChange={e => updateField('description', e.target.value)}
          />
        </FormGroup>

        <div className="d-flex flex-wrap gap-4 mt-3">
          {isAllowance && (
            <div className="d-flex align-items-center">
              <Input
                type="checkbox"
                className="mt-0"
                checked={benefitData.is_taxable}
                onChange={e => updateField('is_taxable', e.target.checked)}
              />
              <Label className="ms-2 mb-0">{t('hr.benefits.taxable')}</Label>
            </div>
          )}
          <div className="d-flex align-items-center">
            <Input
              type="checkbox"
              className="mt-0"
              checked={benefitData.affects_social_security}
              onChange={e => updateField('affects_social_security', e.target.checked)}
            />
            <Label className="ms-2 mb-0">{t('hr.benefits.affectsSocialSecurity')}</Label>
          </div>
          <div className="d-flex align-items-center">
            <Input
              type="checkbox"
              className="mt-0"
              checked={benefitData.is_active}
              onChange={e => updateField('is_active', e.target.checked)}
            />
            <Label className="ms-2 mb-0">{t('common.active')}</Label>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <DefaultButton color="secondary" size="sm" outline onClick={toggle}>{t('common.cancel')}</DefaultButton>
        <DefaultButton
          color="primary"
          size="sm"
          onClick={saveBenefitType}
          loading={saveLoading}
          disabled={saveLoading || !benefitData.name || benefitData.value === ''}
        >{t('common.save')}</DefaultButton>
      </ModalFooter>
    </Modal>
  );
}

export default CreateBenefitTypeModal;
